import type { FC } from 'react';
import dynamic from 'next/dynamic';

import type { HomePageSearchManagerProps } from '../../../components/HomePage/Layouts/HomePageSearchManager';
import { HeroImage } from '../../../components/PageWrappers/SharedComponents/HeroImage/HeroImage';
import type { TabSelectorProps } from '../../../components/Toolkit/TabSelector/TabSelector';
import * as Styled from '../../../components/HomePage/styles/HomePage.styled';
import * as HOMEPAGE from '../../../components/HomePage/helpers/constants';
import type { HeroProps } from '../../../features/landing/Hero/Hero.typed';
import { LocationProvider } from '../../../features/location/Location.context';
import {
  RADIUS_OPTIONS,
  UPDATED_LOCATIONS,
} from '../../../features/location/Location.constants';
import styled from 'styled-components';
import { media } from '../../../helpers/breakpoints';
import { DoneDealCertifiedTrustedDealerLink } from '../../../components/HomePage/components/DoneDealCertifiedTrustedDealerLink/DoneDealCertifiedTrustedDealerLink';
import { VERTICAL } from '../../../helpers/verticals';
import { DisplayContainer } from '../../../components/Support/DisplayContainer/DisplayContainer';
import { theme } from '../../../helpers/Theme';
import { useVerticalContext } from '../../../contexts/VerticalContext';

const HomePageSearchManager = dynamic<HomePageSearchManagerProps>(() =>
  import(
    /* webpackChunkName: "HomePageSearchManager" */ '../../../components/HomePage/Layouts/HomePageSearchManager'
  ).then((mod) => mod.HomePageSearchManager),
);

const HeroWrapper = styled.div<{ vertical: VERTICAL }>`
  --hero-image-height: 460px;
  --hero-search-form-padding: ${({ theme }) => theme.spacing.M16};
  --hero-search-cta-height: 48px;
  // The height is set so that the search CTA is "cut in half" by the end of the background image
  --hero-motor-trusted-dealer-link-and-margin-height: 40px;
  --hero-wrapper-height: calc(
    var(--hero-image-height) + var(--hero-search-form-padding) +
      calc(var(--hero-search-cta-height) / 2) +
      ${({ vertical }) =>
        vertical === VERTICAL.MOTOR
          ? 'var(--hero-motor-trusted-dealer-link-and-margin-height)'
          : '0px'}
  );
  position: relative;
  background-color: white;
  height: var(--hero-wrapper-height);
  display: flex;
  flex-direction: column;

  ${media.large} {
    --hero-image-height: 444px;
    --hero-search-form-padding: ${({ theme }) => theme.spacing.M24};
    --hero-wrapper-height: calc(
      var(--hero-image-height) + var(--hero-search-form-padding) +
        calc(var(--hero-search-cta-height) / 2)
    );
  }
`;

const HeroContentWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-top: auto;
`;

const LAST_SEARCH_DATA_TRACKING_VALUE = 'last_search_home_page';

function getHeroTitle(vertical: VERTICAL) {
  switch (vertical) {
    case 'motor':
      return (
        <>
          Buying a car <br /> should feel this good
        </>
      );
    case 'farming':
      return (
        <>
          Where farmers <br /> do business
        </>
      );
    case 'marketplace':
      return (
        <>
          <DisplayContainer
            config={{ small: 'block', medium: 'block', large: 'none' }}
          >
            Ireland's favourite place <br /> to buy and sell
          </DisplayContainer>
          <DisplayContainer
            config={{ small: 'none', medium: 'none', large: 'block' }}
          >
            Ireland's favourite <br /> place to buy and sell
          </DisplayContainer>
        </>
      );
  }
}

const Hero = (props: HeroProps) => {
  const { vertical, updateVertical } = useVerticalContext();

  const {
    cdnUrl,
    domain,
    adCount,
    lastSearchData,
    setLastSearch,
    motorSearchFilters,
  } = props;
  return (
    <HeroWrapper vertical={vertical}>
      <HeroImage section={vertical} cdnUrl={cdnUrl} />
      <HeroContentWrapper className="row">
        <div className="columns small-12">
          <Styled.HeroContainer>
            <Styled.HeroTitleWrapper>
              <Styled.HeroTitle>{getHeroTitle(vertical)}</Styled.HeroTitle>
              <DisplayContainer
                config={{ small: 'none', medium: 'none', large: 'block' }}
              >
                {vertical === VERTICAL.MOTOR && (
                  <DoneDealCertifiedTrustedDealerLink colorVariant="WHITE" />
                )}
              </DisplayContainer>
            </Styled.HeroTitleWrapper>
            <Styled.HeroContent>
              <Styled.STabSelector<FC<TabSelectorProps<VERTICAL>>>
                onSelectTab={(tab) => updateVertical(tab)}
                selectedTab={vertical}
                tabs={HOMEPAGE.TABS}
                sliderColor={theme.colors.BLUE}
                variant
              />
              <LocationProvider
                countyList={UPDATED_LOCATIONS}
                radiusList={RADIUS_OPTIONS}
              >
                <HomePageSearchManager
                  domain={domain}
                  vertical={vertical}
                  lastSearchData={lastSearchData}
                  setLastSearch={setLastSearch}
                  lastSearchDataTrackingValue={LAST_SEARCH_DATA_TRACKING_VALUE}
                  adCount={adCount}
                  motorSearchFilters={motorSearchFilters}
                />
              </LocationProvider>
            </Styled.HeroContent>
          </Styled.HeroContainer>
        </div>
      </HeroContentWrapper>
    </HeroWrapper>
  );
};

export { Hero };
